import {isFeatureEnabled} from '@github-ui/feature-flags'
import {CollapsibleSidebar} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {CollapsibleFeedbackLink} from '@github-ui/repos-issues-sidebar/CollapsibleFeedbackLink'
import {SIDEBAR_STORAGE_KEY} from '@github-ui/repos-issues-sidebar/constants'
import type {CollapsibleLeftPaneConfig, PaneWithAriaLabel} from '@github-ui/three-panes-layout'

import {RepoIssuesSidebar} from './RepoIssuesSidebar'

const SIDEBAR_ARIA_LABEL = 'Issues sidebar navigation'

/**
 * Shared sidebar configuration for every page that renders `RepoIssuesSidebar`
 * inside a `ThreePanesLayout`.
 *
 * Flag matrix:
 * - `issue_pinned_views` OFF → no sidebar (returns {}). The existing pages
 *   render without a left pane.
 * - `issue_pinned_views` ON + `issues_react_collapsible_sidebar` OFF → render
 *   the sidebar in a fixed non-collapsible left pane (legacy behaviour).
 *   Feedback link is still shown.
 * - `issue_pinned_views` ON + `issues_react_collapsible_sidebar` ON → render
 *   the sidebar inside a `CollapsibleSidebar` with a persisted toggle so the
 *   collapse state is shared across the issues, milestones, labels and
 *   views pages.
 *
 * Not a hook — does not call any hooks. Named with a `get` prefix so the
 * caller can invoke it in any position.
 */
export function getRepoIssuesSidebarPanes(): {
  collapsibleLeftPane?: CollapsibleLeftPaneConfig
  leftPane?: PaneWithAriaLabel
} {
  if (!isFeatureEnabled('issue_pinned_views')) {
    return {}
  }

  if (isFeatureEnabled('issues_react_collapsible_sidebar')) {
    return {
      collapsibleLeftPane: {
        storageKey: SIDEBAR_STORAGE_KEY,
        expandedWidth: 'small',
        render: () => <RepoIssuesSidebar collapsible />,
        renderFooter: ({isExpanded, toggle}) => (
          <>
            <CollapsibleSidebar.Toggle isExpanded={isExpanded} onToggle={toggle} />
            <CollapsibleFeedbackLink isExpanded={isExpanded} />
          </>
        ),
      },
    }
  }

  return {
    leftPane: {
      element: <RepoIssuesSidebar />,
      ariaLabel: SIDEBAR_ARIA_LABEL,
    },
  }
}
